import { ActivatedRoute } from "@angular/router";
import {
  checkIfTokenValid,
  getCustomerWithLongestPass,
  getFormControls,
  model2Doc,
} from "../api.service";
import { checkPasswordPolicy } from "../password/password-policy";
import { Component, OnInit, Output, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { DocModel } from "../state/state";
import { getApp } from "../app";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { NO_ERROR } from "../users/users.service";
import { ResetPasswordObject } from "../../../../shared/models/reset-password-object";
import { Subject } from "rxjs";
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { ResetPasswordEmailDetails } from "../../../../shared/models/reset-password-email-details";
import { MustMatch } from "../core/validation/must-match.validator";
import { NoWhitespaceValidator } from "../core/validation/no-whitespace.validator";

@Component({
    selector: "app-reset-password",
    templateUrl: "./reset-password.component.html",
    styleUrls: ["./reset-password.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResetPasswordComponent implements OnInit {
  @ViewChild("resetPasswordModal", { static: true })
  resetPasswordModal: any;
  @ViewChild("content", { static: true }) content: any;
  @Output() ok = new Subject<any>();
  resetForm: FormGroup;
  passwordForm: FormGroup;

  app = getApp((app) => {
    this.app = app;
  });

  model: DocModel = {};
  routeParams: any;
  tokenValid = false;
  tokenHasBeenUsed = false;
  hasSuccess = false;

  constructor(
    private route: ActivatedRoute,
    private modalService: NgbModal,
    private formBuild: FormBuilder
  ) {
    this.routeParams = this.route.snapshot.params;

    this.resetForm = this.formBuild.group({
      email: ["", [Validators.required, Validators.email]],
    });
    this.passwordForm = this.formBuild.group(
      {
        password: [""],
        retypePassword: ["", [Validators.required]],
      },
      {
        validator: MustMatch("password", "retypePassword"),
      }
    );
  }
  async ngOnInit() {
    // Update favicon
    this.app.environment.setFavicon(document.getElementById("favicon")!);

    // Update title
    this.app.environment.setTitle();
    this.initValidators();

    if (this.routeParams.token != null && this.routeParams.token !== "") {
      let result = await checkIfTokenValid(this.routeParams.token);
      this.app.auth.passwordLength = (result as any).passLength;

      this.tokenValid = (result as any).ok;
      this.open();

      if (!this.tokenValid) {
        setTimeout(() => {
          this.modalService.dismissAll();
          this.app.routing.navigateLogin();
        }, 3000);
      }
    }
  }

  initValidators() {
    this.passwordForm.controls.password.setValidators([
      Validators.minLength(this.app.auth.passwordLength),
      Validators.required,
      NoWhitespaceValidator,
      Validators.pattern(
        "^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[^A-Za-z0-9]).{8,}$"
      ),
    ]);
  }

  async open() {
    try {
      this.hasSuccess = false;
      this.app.field.isValidRegex("user.email", this.model["user.email"]);
      this.initValidators();
      const result = await this.modalService.open(this.content, {
        windowClass: "mediumModal",
        backdrop: "static",
        keyboard: false,
      }).result;
      this.ok.next(result);
    } catch (err) {
      // NOP
    }
  }

  get requiredFields() {
    this.app.field.resetAllRequiredFieldSettings("user");
    this.app.field.getFieldSettings("user.email").required = true;
    this.app.field.getFieldSettings("user.email").mutable = true;
    if (this.app.view === "home") {
      this.app.field.getFieldSettings("user.password").required = true;
    }
    const requiredFields = this.app.field.getInvalidFields(
      "user",
      this.app.model
    );
    return requiredFields;
  }

  async requestChangePass() {
    let controls = getFormControls(this.resetForm);
    let email = controls.email.value.toLowerCase().trim();
    this.model["user.email"] = email;

    const checkEmail = this.validateEmail();
    if (checkEmail) {
      let customer: any;
      try {
        customer = await getCustomerWithLongestPass(email);
      } catch (err) {
        let error = JSON.parse((err as any).error).message;
        this.app.hasError = true;
        (this.app.errorText as any) = this.getErrorText(error);
        customer = null;
      }
      const doc: ResetPasswordEmailDetails = {
        emailAddress: email,
        webUrl: "",
        customer: customer,
      };
      if (customer != null) {
        try {
          this.hasSuccess = true;
          await this.app.auth.resetPassword(doc);
          this.app.hasSuccess = true;
          this.app.successText = this.app.text.resetPassword.messageSent;
          setTimeout(() => {
            this.app.state.next({ hasSuccess: false, errorText: "" });
          }, 3000);
        } catch (error) {
          this.app.hasError = true;
          this.app.errorText = this.app.text.resetPassword.userNotFound;
          setTimeout(() => {
            this.app.state.next({ hasError: false, errorText: "" });
          }, 3000);
        }
      }
    } else {
      this.app.hasError = true;
      this.app.errorText = this.app.text.resetPassword.emailNotCorrect;
      setTimeout(() => {
        this.app.state.next({ hasError: false, errorText: "" });
      }, 3000);
    }
    this.model = {};
    this.resetForm.controls.email.setValue("");
    this.modalService.dismissAll();
  }

  validateEmail() {
    const re = /\S+@\S+\.\S+/;
    let result = re.test(this.model["user.email"]);
    return result;
  }

  getErrorText(error: any) {
    switch (error) {
      case "user not found":
        return this.app.text.resetPassword.userNotFound;
      case "customer settings not found":
        return this.app.text.resetPassword.userSettingsNotFound;
    }
  }

  async changePassword() {
    let controls = getFormControls(this.passwordForm);
    this.model["user.password"] = controls.password.value;
    this.model["user.retypePassword"] = controls.retypePassword.value;

    const passwordObject = model2Doc("user", this.model);
    const checkPassword = await checkPasswordPolicy(
      this.app.text,
      passwordObject,
      this.app.customers.expectCurrent
    );
    if (checkPassword !== NO_ERROR) {
      this.app.hasError = true;
      this.app.errorText = checkPassword;
      setTimeout(() => {
        this.app.state.next({ hasError: false, errorText: "" });
      }, 3000);
    } else {
      const doc: ResetPasswordObject = {
        password: this.model["user.password"],
        retypePassword: this.model["user.retypePassword"],
        token:
          this.routeParams.token != null
            ? this.routeParams.token
            : this.app.state.userId,
      };
      const result = await this.app.auth.savePassword(doc);
      this.passwordForm.controls.password.setValue("");
      this.passwordForm.controls.retypePassword.setValue("");
      this.app.auth.loginBtnDisabled = false;
      if (result) {
        this.app.hasSuccess = true;
        this.app.successText = "User saved successfully!";
        this.app.state.next({
          passwordChangeRequired: false,
        });
        this.modalService.dismissAll();
        this.app.routing.navigateLogin();
        setTimeout(() => {
          this.app.state.next({ hasSuccess: false, successText: "" });
        }, 3000);
      } else {
        this.app.hasError = true;
        this.app.errorText = this.app.text.user.passwordUsed;
        setTimeout(() => {
          this.app.state.next({ hasError: false, errorText: "" });
        }, 3000);
      }
    }
  }
}
